"use client";

import { useState } from "react";
import Link from "next/link";
import { QUALIFICATIONS_TREE, ROLES_TREE } from "@/lib/taxonomy";
import { CascadingTree } from "@/app/components/CascadingTree";
import { submitCourse } from "@/app/actions/courses";

export function CourseSubmitForm() {
  const [title, setTitle] = useState("");
  const [providerName, setProviderName] = useState("");
  const [providerUrl, setProviderUrl] = useState("");
  const [description, setDescription] = useState("");
  const [qualifications, setQualifications] = useState<Set<string>>(new Set());
  const [seasonTypes, setSeasonTypes] = useState<Set<string>>(new Set());
  const [roles, setRoles] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState("in-person");
  const [location, setLocation] = useState("");
  const [duration, setDuration] = useState("");
  const [priceFrom, setPriceFrom] = useState("");
  const [bookingUrl, setBookingUrl] = useState("");
  const [submittedByEmail, setSubmittedByEmail] = useState("");
  const [submittedByName, setSubmittedByName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await submitCourse({
      title: title.trim(),
      providerName: providerName.trim(),
      providerUrl: providerUrl.trim(),
      description: description.trim(),
      qualifications: Array.from(qualifications),
      seasonTypes: Array.from(seasonTypes),
      roles: Array.from(roles),
      format,
      location: location.trim(),
      duration: duration.trim(),
      priceFrom: priceFrom ? parseInt(priceFrom, 10) : null,
      bookingUrl: bookingUrl.trim(),
      submittedByEmail: submittedByEmail.trim(),
      submittedByName: submittedByName.trim(),
    });

    setLoading(false);
    if (result.error) {
      setError(result.error);
    } else {
      setSuccess(true);
    }
  }

  function toggleSeason(id: string) {
    setSeasonTypes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (success) {
    return (
      <div className="max-w-[560px] mx-auto px-5 md:px-10 py-16 text-center">
        <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-5">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-success">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h1 className="font-serif text-[28px] text-navy mb-3">
          Course submitted
        </h1>
        <p className="text-[15px] text-text-muted leading-[1.7] mb-6 max-w-[400px] mx-auto">
          Thanks for submitting your course. We&apos;ll review it and get it
          live within 24 hours. We may reach out if we have questions.
        </p>
        <div className="flex gap-3 justify-center">
          <Link
            href="/plan/courses"
            className="bg-gold text-white px-6 py-2.5 rounded-sm text-[14px] font-semibold no-underline font-sans transition-all hover:bg-gold-light"
          >
            Browse Courses
          </Link>
          <button
            onClick={() => {
              setSuccess(false);
              setTitle("");
              setProviderName("");
              setProviderUrl("");
              setDescription("");
              setQualifications(new Set());
              setSeasonTypes(new Set());
              setRoles(new Set());
              setFormat("in-person");
              setLocation("");
              setDuration("");
              setPriceFrom("");
              setBookingUrl("");
            }}
            className="text-navy px-6 py-2.5 rounded-sm text-[14px] font-semibold border-[1.5px] border-navy/18 bg-transparent cursor-pointer font-sans transition-all hover:border-navy"
          >
            Submit Another
          </button>
        </div>
      </div>
    );
  }

  const inputClass =
    "w-full px-4 py-3 border-[1.5px] border-border rounded-[10px] text-sm font-sans text-charcoal bg-warm-bg outline-none focus:border-gold focus:bg-white placeholder:text-[#B0A99F] transition-all";

  return (
    <div className="max-w-[640px] mx-auto px-5 md:px-10 pb-20">
      <div className="pt-6 pb-4">
        <Link
          href="/plan/courses"
          className="text-[13px] text-text-muted no-underline hover:text-gold transition-colors inline-flex items-center gap-1"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          All Courses
        </Link>
      </div>

      <h1 className="font-serif text-[28px] sm:text-[36px] text-navy mb-2">
        Submit a Course
      </h1>
      <p className="text-[15px] text-text-muted mb-8 max-w-[480px]">
        List your training course or qualification on PeakWave. Free to list, no
        commissions. We&apos;ll review and publish within 24 hours.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Course Details */}
        <div>
          <h2 className="font-serif text-[18px] text-navy mb-4">
            Course details
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                Course Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. BASI Level 1 Ski Instructor Course"
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What is this course? Who is it for? What will students learn?"
                rows={4}
                className={`${inputClass} resize-y`}
              />
            </div>
          </div>
        </div>

        {/* Provider Details */}
        <div>
          <h2 className="font-serif text-[18px] text-navy mb-4">
            Provider details
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                Provider Name *
              </label>
              <input
                type="text"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
                placeholder="e.g. Basecamp Group"
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                Provider Website
              </label>
              <input
                type="url"
                value={providerUrl}
                onChange={(e) => setProviderUrl(e.target.value)}
                placeholder="https://..."
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Qualifications & Roles */}
        <div>
          <h2 className="font-serif text-[18px] text-navy mb-4">
            Qualifications & Roles
          </h2>
          <div className="space-y-4">
            <CascadingTree
              tree={QUALIFICATIONS_TREE}
              selected={qualifications}
              onChange={setQualifications}
              label="Qualifications Awarded"
              maxHeight="240px"
              searchable
            />
            <CascadingTree
              tree={ROLES_TREE}
              selected={roles}
              onChange={setRoles}
              label="Relevant Roles"
              maxHeight="240px"
              searchable
            />
          </div>
        </div>

        {/* Season Types */}
        <div>
          <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-2">
            Relevant Seasons
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "winter", label: "Winter", emoji: "❄️" },
              { id: "summer", label: "Summer", emoji: "☀️" },
              { id: "festivals", label: "Festivals", emoji: "🎪" },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => toggleSeason(s.id)}
                className={`px-4 py-2.5 rounded-sm text-[14px] font-medium border cursor-pointer font-sans transition-all ${
                  seasonTypes.has(s.id)
                    ? "bg-gold/10 text-gold border-gold/40"
                    : "bg-white text-text-body border-border hover:border-gold/30"
                }`}
              >
                {s.emoji} {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Logistics */}
        <div>
          <h2 className="font-serif text-[18px] text-navy mb-4">Logistics</h2>
          <div className="space-y-4">
            <div>
              <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                Format *
              </label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                className={`${inputClass} cursor-pointer`}
              >
                <option value="in-person">In Person</option>
                <option value="online">Online</option>
                <option value="both">Online & In Person</option>
              </select>
            </div>
            {format !== "online" && (
              <div>
                <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                  Location
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. Meribel, France"
                  className={inputClass}
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                  Duration
                </label>
                <input
                  type="text"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g. 10 weeks"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                  Starting Price (&pound;)
                </label>
                <input
                  type="number"
                  value={priceFrom}
                  onChange={(e) => setPriceFrom(e.target.value)}
                  placeholder="e.g. 2500"
                  min="0"
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                Booking / Info URL
              </label>
              <input
                type="url"
                value={bookingUrl}
                onChange={(e) => setBookingUrl(e.target.value)}
                placeholder="https://..."
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div>
          <h2 className="font-serif text-[18px] text-navy mb-4">
            Your details
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                Your Name *
              </label>
              <input
                type="text"
                value={submittedByName}
                onChange={(e) => setSubmittedByName(e.target.value)}
                placeholder="Full name"
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-1.5">
                Your Email *
              </label>
              <input
                type="email"
                value={submittedByEmail}
                onChange={(e) => setSubmittedByEmail(e.target.value)}
                placeholder="email@example.com"
                className={inputClass}
                required
              />
              <p className="text-[11px] text-text-muted mt-1">
                We&apos;ll only use this to contact you about this submission
              </p>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <p className="text-sm text-error font-medium">{error}</p>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 bg-gold text-white rounded-sm text-[15px] font-semibold border-none cursor-pointer font-sans transition-all hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_16px_rgba(28, 138, 153,0.25)]"
        >
          {loading ? "Submitting..." : "Submit Course for Review"}
        </button>
      </form>
    </div>
  );
}
